import React, { useContext } from "react";
import { styled } from "@linaria/react";
import { GAME_LIST } from "../../games-list";
import { getSearchTags } from "../search/SearchBox";
import { GameIcon, KindIcon } from "../kind-icon/KindIcon";
import { CardBlock, InlineList, PageHeader, PageTitle } from "./styles";
import { highlightLink } from "./link-styles";
import { Link } from "../Link";
import { DeclarationsContext, schemaPath } from "./DeclarationsContext";

const InfoBlock = styled(CardBlock)`
  margin-top: 32px;

  p {
    margin: 0;
  }

  p + p {
    margin-top: 8px;
  }
`;

const InfoLink = styled.a`
  ${highlightLink}

  &:hover {
    color: var(--text);
  }
`;

const SearchFiltersBlock = styled(CardBlock)`
  dl {
    margin: 0;
  }

  dt {
    font-weight: 600;
  }

  dd {
    margin: 0 0 8px 12px;
  }

  dd:last-child {
    margin-bottom: 0;
  }

  code {
    background: var(--group-members);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 15px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  max-width: 1140px;
  margin: 32px auto 0;

  ${InfoBlock}, ${SearchFiltersBlock} {
    flex: 1 1 360px;
    margin-top: 0;
  }
`;

const HomepageSearchFilters = styled(SearchFiltersBlock)`
  margin-top: 32px;
`;

const HomepageHeader = styled(PageHeader)`
  margin: 32px 0 0;
`;

const HomepageCard = styled(CardBlock)`
  margin-top: 32px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  p {
    margin: 0;
  }

  p + p {
    margin-top: 8px;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`;

function GameList() {
  return (
    <InlineList>
      {GAME_LIST.map((g) => (
        <Link key={g.id} to={schemaPath(g.id)}>
          <GameIcon game={g.id} />
          {g.name}
        </Link>
      ))}
    </InlineList>
  );
}

function SearchFilters({ isRoot }: { isRoot?: boolean }) {
  const Block = isRoot ? HomepageSearchFilters : SearchFiltersBlock;
  const { entities, hasNetwork } = useContext(DeclarationsContext);
  const hasEntities = entities.length > 0;
  const tags = getSearchTags("schemas", { hasEntities, hasNetwork });
  return (
    <Block>
      <dl>
        <dt>Search by name, filters can be combined</dt>
        <dd>
          Type any text to match class, field, or enum names
          {hasEntities && ", or entity design names"}.
        </dd>
        {tags.map((t) => (
          <React.Fragment key={t.tag}>
            <dt>
              <KindIcon kind={t.icon} /> <code>{t.tag}</code> — {t.description.toLowerCase()}
            </dt>
            <dd>{t.example}</dd>
          </React.Fragment>
        ))}
      </dl>
    </Block>
  );
}

export function SchemaHome({ isRoot }: { isRoot: boolean }) {
  if (isRoot) {
    return (
      <>
        <HomepageContent />
        <SearchFilters isRoot />
      </>
    );
  }

  return (
    <InfoRow>
      <InfoBlock>
        <p>
          Source 2 includes a schema system that describes the engine's classes, fields, and
          enumerations along with their types, offsets, and metadata. These schemas comprehensively
          map engine internals, making them useful for modding and reverse engineering.
        </p>
        <p>
          The schemas displayed here are generated by{" "}
          <InfoLink href="https://github.com/ValveResourceFormat/DumpSource2">DumpSource2</InfoLink>{" "}
          and automatically updated by{" "}
          <InfoLink href="https://github.com/SteamTracking/GameTracking">GameTracking</InfoLink>.
          The code for this site is on{" "}
          <InfoLink href="https://github.com/ValveResourceFormat/SchemaExplorer">GitHub</InfoLink>.
        </p>
        <p>Currently tracking:</p>
        <GameList />
      </InfoBlock>
      <SearchFilters />
    </InfoRow>
  );
}

function HomepageContent() {
  return (
    <>
      <HomepageHeader>
        <PageTitle>Source 2 Schema Explorer</PageTitle>
      </HomepageHeader>
      <HomepageCard>
        <h2>What is the Source 2 schema system?</h2>
        <p>
          Source 2's schema system (schemasystem.dll) is the engine's runtime reflection system. It
          works like RTTI on steroids: every class, field, enum, offset, and metadata annotation is
          exposed for introspection. If you're familiar with Source 1's netvars, schemas go much
          further: you get the full memory layout of all classes, not just networked ones, along
          with static members and inheritance chains.
        </p>
        <p>
          Game modules like client.dll and server.dll each register types into their own type scope.
          These hold field layouts, base class info, alignment, size, and metadata like network
          groups and default values.
        </p>
      </HomepageCard>

      <HomepageCard>
        <h2>What can you find here?</h2>
        <p>
          Look up any class structure or enum the engine exposes, see inheritance hierarchies, and
          check metadata like network groups, encoding functions, and default values. The data is
          extracted offline from game files by{" "}
          <InfoLink href="https://github.com/ValveResourceFormat/DumpSource2">DumpSource2</InfoLink>{" "}
          and updated automatically with each game patch. You'll sometimes see this called a "schema
          dump". The code for this site is open source on{" "}
          <InfoLink href="https://github.com/ValveResourceFormat/SchemaExplorer">GitHub</InfoLink>.
        </p>
        <GameList />
      </HomepageCard>

      <HomepageCard>
        <h2>Who uses Source 2 schemas?</h2>
        <p>
          <InfoLink href="https://s2v.app/">Source 2 Viewer</InfoLink> relies on schemas to parse
          compiled resource files like models, maps, and particles. Modders and tool developers use
          them for everything from custom game modes to spectator overlays and replay parsers.
          Community tools like{" "}
          <InfoLink href="https://github.com/GAMMACASE/Source2SchemaDumper">
            Source2SchemaDumper
          </InfoLink>{" "}
          and <InfoLink href="https://github.com/a2x/cs2-dumper">cs2-dumper</InfoLink> generate C++,
          C#, Rust, JSON, and KV3 SDKs from schema data.
        </p>
        <p>
          Schemas change with every game update. This site pulls from{" "}
          <InfoLink href="https://github.com/SteamTracking/GameTracking">GameTracking</InfoLink>{" "}
          whenever a new build ships. Schema diffs between updates are available on GitHub:{" "}
          <InfoLink href="https://github.com/SteamTracking/GameTracking-CS2/tree/master/DumpSource2/schemas">
            CS2
          </InfoLink>
          ,{" "}
          <InfoLink href="https://github.com/SteamTracking/GameTracking-Dota2/tree/master/DumpSource2/schemas">
            Dota 2
          </InfoLink>
          ,{" "}
          <InfoLink href="https://github.com/SteamTracking/GameTracking-Deadlock/tree/master/DumpSource2/schemas">
            Deadlock
          </InfoLink>
          .
        </p>
      </HomepageCard>
    </>
  );
}
